import type { Marker } from 'mapbox-gl';
import type FeatureData from './FeatureData';
import type { MapFeature } from './FeatureData';

/** Clave con la que se indexa un elemento, segun keyField. */
export type ListKey = string | number;

/**
 * Elemento de una lista: un dato de mapa que ademas puede tener marcador.
 * Lo cumplen CityData, HotelData, POIData, RouteData y StageData.
 */
export interface ListItem extends FeatureData {
    mapMarker?: Marker | null;
}

/**
 * Lee la clave indicada por keyField, que es dinamica ('id', 'route_id'...).
 * Los datos de mapa no declaran indice por cadena, asi que se asevera aqui.
 */
function readKey(item: ListItem, keyField: string): ListKey {
    return (item as unknown as Record<string, ListKey>)[keyField];
}

/**
 * Wrapper to manage generic lists of places 
 */
class BaseList<T extends ListItem = ListItem>  {    
    /**
     * PlaceData items indexed by id
     */
    dataMap: Map<ListKey, T>;
    
    // Max list size.
    maxSize = 100;

    /**
     * Other referenced objects. Declarados de siempre, pero nadie los asigna
     * ni los lee.
     */
    mapObject?: unknown;
    mapComponent?: unknown;
    
    // Selected id
    selectedId?: ListKey | null;
    
    // Key field
    keyField = 'id';
        
    /**
     * Build from data Items.
     */
    constructor(placeDataList: T[] = [], keyField = 'id') {
      this.dataMap = new Map(placeDataList.map(i => [readKey(i, keyField), i]));
    } 
    
    /**
     * Add place item.
     */
    addPlace(placeData: T, replace = true): this {
      if (replace || !this.dataMap.has(placeData.id)) {
        this.dataMap.set(readKey(placeData, this.keyField), placeData);
      }
      return this;
    }
    
    /**
     * Delete element and check selection.
     */
    deleteId(id: ListKey): this {
      if (this.selectedId && this.selectedId === id) {
        this.cleanSelection();
      }
      this.dataMap.delete(id);
      return this;
    }
    
    /**
     * Check max size, delete items if too many.
     */
    checkSize(): ListKey[] | undefined {
      if (this.dataMap.size > this.maxSize) {
        const deleteKeys = Array.from(this.dataMap.keys()).slice(0, this.dataMap.size - this.maxSize);
        deleteKeys.forEach(key => {this.deleteId(key)});
        return deleteKeys;
      }
      return undefined;
    }
    
    /**
     * Count items
     */
    countItems(): number {
      return this.dataMap.size;
    }
    
    
    /**
     * Add multiple place items.
     */
    addPlaceList(placeList: T[]): this {
      placeList.forEach(item => {this.addPlace(item)});
      this.checkSize();
      return this;
    }
    
    /**
     * Get place by id.
     */
    getPlace(id: ListKey): T | undefined {
      return this.dataMap.get(id);
    }
    
    
    /**
     * Gets first item.
     */
    getFirst(): T | undefined {
      if (this.dataMap.size) {
        // El tamano comprobado arriba garantiza que hay una primera clave.
        return this.getPlace(this.dataMap.keys().next().value!);
      }
      return undefined;
    }    
    
    /**
     * Clean all data.
     */
    clean(): this {
        this.cleanSelection();
        this.cleanData();
        return this;
    }
    
     /**
     * Clean all data.
     */
    cleanData(): this {
        this.dataMap.clear();
        return this;
    }
       
    /**
     * Clean selected items.
     */
    cleanSelection(): void {
        this.setSelectedId(null);
    }
    
    /**
     * Check for selected item.
     *
     * Devuelve un valor veraz/falso, no un boolean estricto: si no hay
     * seleccion devuelve el propio selectedId (null o undefined).
     */
    hasSelection() {
        return this.selectedId && this.dataMap.has(this.selectedId);
    }
    
    /**
     * Gets selected map features.
     */
    getSelectedFeatures(): MapFeature[] {
      const selected = this.getSelectedPlace();
      if (selected && selected.mapFeature) {
        return [selected.mapFeature];
      }
      else {
        return [];
      }
    }
    
    /**
     * Gets selected marker
     */
    
    /**
     * Set and highlight selected item id 
     */
    setSelectedId(id: ListKey | null): this {
      this.selectedId = id;
      return this; 
    }
    
    /**
     * Gets selected Place
     */
    getSelectedPlace(): T | null | undefined {
      return this.selectedId ? this.getPlace(this.selectedId) : null;
    }
    
    /**
     * Selected place data.
     *
     * Nota: addPlace indexa por keyField, pero aqui se selecciona por id. En
     * listas con keyField distinto de 'id' (RouteList) no coinciden.
     */
    setSelectedPlace(placeData: T): this {
      this.addPlace(placeData);
      this.setSelectedId(placeData.id);
      // this.setSelectedMarker(placeData.mapMarker);
      return this;
    } 
    
    /**
     * Gets data items as array
     */
    getDataItems(): T[] {
      return Array.from(this.dataMap.values());
    }
    
    /**
     * Clean all item's markers
     */
    cleanMarkers(): void {
       this.getDataItems().forEach(async (item) => {
            item.mapMarker = null;
            /*
            const el = item.createElement();
            el.addEventListener('click', (event) => {
                this.handleHotelClick(item, event);
            });              
            item.addToMap(map);
            */
        });
    }
    
}

export default BaseList;
